import clsx from 'clsx';
import React, { Fragment } from 'react';

import { MidArticleEventProps } from '@components/pages/Article/MidArticle/Event/interfaces';
import { useTimeline } from '@hooks';
import useIframeResizeMessager from '@hooks/useIframeResizeMessager';
import useInlineLinkTracking from '@hooks/useInlineLinkTracking';
import useSlickCarousel from '@hooks/useSlickCarousel';
import { Author } from '@interfaces/authors';
import { Article, ParsedTakeaway, Pickup, ScriptList } from '@interfaces/homepage';
import { TrackingLocation } from '@tracking';
import {
  AuthoredArticleContentReactToHtmlParser,
  AuthoredContentReactToHtmlParser,
} from '@utils/reactHtmlParserTransform';

import FadeWrapper from '../../FadeWrapper';
import Takeaway from './Takeaway';
import styles from './styles.module.scss';
import useLoadScripts from './useLoadScripts';

export const ARTICLE_OPINION_HIGHLIGHT = 'Opinion';
export const ARTICLE_TOP_POSITION = 'top';
export const ARTICLE_BOTTOM_POSITION = 'bottom';

interface BodyProps {
  addendum?: string | null;
  authors: Author[];
  canViewFullArticle: boolean;
  className?: string;
  freeBlurb: string;
  fullText?: string | null;
  isSEOBotAndArticleIsNotFree: boolean;
  highlight?: string;
  pickups?: Pickup[];
  relatedArticles?: Article[];
  relatedEvent?: MidArticleEventProps;
  scriptList?: ScriptList[];
  takeaway?: ParsedTakeaway[];
}

const Body = ({
  addendum,
  authors,
  canViewFullArticle,
  className,
  freeBlurb,
  fullText,
  isSEOBotAndArticleIsNotFree,
  highlight,
  relatedArticles,
  relatedEvent,
  scriptList,
  takeaway,
}: BodyProps) => {
  useInlineLinkTracking({
    trackingLocation: TrackingLocation.ContentBody,
    targetSelector: '.clipperable-content',
  });
  useIframeResizeMessager();
  useTimeline();
  useLoadScripts({ scriptList });
  useSlickCarousel();

  const renderAuthorBio = (position: string, author: Author, highlight: string) => (
    <p className="font-noto-serif text-body-articles !font-light italic last-of-type:mb-0">
      {AuthoredContentReactToHtmlParser(
        position === ARTICLE_TOP_POSITION
          ? author.bio
          : highlight === ARTICLE_OPINION_HIGHLIGHT && author.fullBio
          ? author.fullBio
          : author.bio
      )}
    </p>
  );

  return (
    <div className={clsx(styles.articleBody, 'clipperable-content break-words')}>
      {highlight === ARTICLE_OPINION_HIGHLIGHT && authors.length > 0 && (
        <div className="mb-28 border-0 border-b border-solid border-grayscale-beige pb-28 sm:mb-48 sm:pb-48">
          {authors.map((author, index) => (
            <Fragment key={index}>
              {renderAuthorBio(ARTICLE_TOP_POSITION, author, highlight)}
            </Fragment>
          ))}
        </div>
      )}
      {canViewFullArticle ? (
        AuthoredArticleContentReactToHtmlParser({ text: freeBlurb })
      ) : (
        <FadeWrapper>{AuthoredArticleContentReactToHtmlParser({ text: freeBlurb })}</FadeWrapper>
      )}
      {takeaway?.length > 0 && <Takeaway takeaway={takeaway} />}
      {fullText && (
        <div
          className={clsx({ 'seo-paywall-content': isSEOBotAndArticleIsNotFree }, className)}
          data-nosnippet
        >
          {AuthoredArticleContentReactToHtmlParser({
            text: fullText,
            relatedArticles,
            relatedEvent,
          })}
        </div>
      )}
      {canViewFullArticle && (
        <div className="border-0 border-t border-solid border-grayscale-beige pt-28 sm:pt-48">
          {addendum && (
            <p className="!font-suisse-intl !text-button-lg !font-light text-black">
              {AuthoredContentReactToHtmlParser(addendum)}
            </p>
          )}
          {authors.length > 0 &&
            authors.map((author, index) => (
              <Fragment key={index}>
                {renderAuthorBio(ARTICLE_BOTTOM_POSITION, author, highlight)}
              </Fragment>
            ))}
        </div>
      )}
    </div>
  );
};

export default Body;
